import React, { useEffect, useMemo, useRef, useState } from "react";
import { getClassName } from "@sscs/utils";
import { useHasOverlayScrollbars, useIsOverflowing } from "../../hooks";
import { ChevronLeftIcon } from "../../icons";
import { IconButton } from "../../index";
import { SidebarCollapseContext } from "./collapseContext";
import styles from "./navigationSidebar.module.scss";
const STORAGE_PREFIX = "sidebar";
const getStoredWidth = (storageKey) => {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}:${storageKey}:width`);
    const width = stored ? Number(stored) : NaN;
    return Number.isFinite(width) ? width : null;
};
const setStoredWidth = (storageKey, width) => {
    const key = `${STORAGE_PREFIX}:${storageKey}:width`;
    if (width === null) {
        localStorage.removeItem(key);
    }
    else {
        localStorage.setItem(key, String(width));
    }
};
const getStoredCollapsed = (storageKey) => {
    return (localStorage.getItem(`${STORAGE_PREFIX}:${storageKey}:collapsed`) === "true");
};
const setStoredCollapsed = (storageKey, collapsed) => {
    localStorage.setItem(`${STORAGE_PREFIX}:${storageKey}:collapsed`, String(collapsed));
};
export const useSidebarCollapsed = (storageKey) => {
    const [isCollapsed, setIsCollapsed] = useState(() => getStoredCollapsed(storageKey));
    const handleCollapse = (collapsed) => {
        setIsCollapsed(collapsed);
        setStoredCollapsed(storageKey, collapsed);
    };
    return [isCollapsed, handleCollapse];
};
const NavigationSidebar = ({ storageKey, title, defaultWidth = null, minWidth = 250, maxWidth = 500, resizeWidth = true, className, children, isCollapsed, onCollapse, ...props }) => {
    const [sidebarWidth, setSidebarWidth] = useState(() => { var _a; return (_a = getStoredWidth(storageKey)) !== null && _a !== void 0 ? _a : defaultWidth; });
    const [isResizingX, setIsResizingX] = useState(false);
    const sidebarRef = useRef(null);
    const contentRef = useRef(null);
    const hasOverlayScrollbars = useHasOverlayScrollbars();
    const contentOverflowsY = useIsOverflowing(contentRef, "y");
    const needsScrollbarGutter = hasOverlayScrollbars && contentOverflowsY;
    const handleMouseDownX = (event) => {
        event.preventDefault();
        setIsResizingX(true);
    };
    const handleResetWidth = () => {
        setSidebarWidth(null);
        setStoredWidth(storageKey, null);
    };
    useEffect(() => {
        const handleMouseUpX = () => {
            setIsResizingX(false);
            setSidebarWidth((currentWidth) => {
                setStoredWidth(storageKey, currentWidth);
                return currentWidth;
            });
        };
        const handleMouseMoveX = (event) => {
            if (!isResizingX || !sidebarRef.current)
                return;
            let newWidth = event.clientX - sidebarRef.current.getBoundingClientRect().left;
            if (newWidth < minWidth)
                newWidth = minWidth;
            if (newWidth > maxWidth)
                newWidth = maxWidth;
            setSidebarWidth(newWidth);
        };
        if (isResizingX) {
            document.addEventListener("mousemove", handleMouseMoveX);
            document.addEventListener("mouseup", handleMouseUpX);
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMoveX);
            document.removeEventListener("mouseup", handleMouseUpX);
        };
    }, [isResizingX, minWidth, maxWidth, storageKey]);
    const canResize = resizeWidth && !isCollapsed;
    const inlineStyles = {
        width: canResize ? (sidebarWidth !== null && sidebarWidth !== void 0 ? sidebarWidth : undefined) : undefined,
    };
    const collapseState = useMemo(() => ({ isCollapsed: Boolean(isCollapsed) }), [isCollapsed]);
    return (React.createElement(SidebarCollapseContext.Provider, { value: collapseState },
        React.createElement("nav", { ...props, ref: sidebarRef, style: inlineStyles, className: getClassName(styles["sidebar-container"], className, {
                [styles["sidebar-collapsed"]]: Boolean(isCollapsed),
            }) },
            React.createElement("div", { className: getClassName(styles.header, {
                    [styles["header-collapsed"]]: Boolean(isCollapsed),
                }) },
                !isCollapsed && React.createElement("span", { className: styles.title }, title),
                isCollapsed !== undefined && (React.createElement(IconButton, { size: "small", onClick: () => onCollapse === null || onCollapse === void 0 ? void 0 : onCollapse(!isCollapsed), "aria-label": isCollapsed ? "Expand sidebar" : "Collapse sidebar" },
                    React.createElement(ChevronLeftIcon, null)))),
            React.createElement("div", { id: "sidebar-content", ref: contentRef, className: getClassName(styles["sidebar-content"], {
                    [styles["sidebar-content-gutter"]]: needsScrollbarGutter,
                }) }, children),
            canResize && (React.createElement("div", { id: "resize-width", onMouseDown: handleMouseDownX, onDoubleClick: handleResetWidth, className: styles["resize-width"] }, "\u22EE")))));
};
export default NavigationSidebar;
//# sourceMappingURL=NavigationSidebar.js.map